// src/pages/FulfillmentPage/components/DetailsCard.tsx
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Avatar,
  alpha,
  useTheme,
  Paper,
  Divider,
  Chip
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import NotesIcon from "@mui/icons-material/Notes";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import { Fulfillment, Driver } from "../types";
import { formatPhone } from "../utils/helpers";

interface DetailsCardProps {
  fulfillment: Fulfillment;
  isLocalDelivery: boolean;
  drivers: Driver[];
  driverLoading: boolean;
  actionLoading: boolean;
  selectedDriverId: number | "";
  onDriverChange: (id: number) => void;
  onAssignDriver: () => void;
  onUpdateNotes?: (notes: string) => Promise<void>;
}

export const DetailsCard: React.FC<DetailsCardProps> = ({
  fulfillment,
  isLocalDelivery,
  drivers,
  driverLoading,
  actionLoading,
  selectedDriverId,
  onDriverChange,
  onAssignDriver,
  onUpdateNotes,
}) => {
  const theme = useTheme();
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState(fulfillment.packing_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  const handleSaveNotes = async () => {
    if (!onUpdateNotes) return;
    
    setSavingNotes(true);
    try {
      await onUpdateNotes(notes);
      setIsEditingNotes(false);
    } catch (error) {
      console.error("Failed to update notes:", error);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCancelNotes = () => {
    setNotes(fulfillment.packing_notes || "");
    setIsEditingNotes(false);
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 3, 
        mb: 2, 
        width: "100%", 
        overflow: "hidden",
        border: '1px solid',
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.2),
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.05)}`
        }
      }}
    >
      {/* Header with colored accent */}
      <Box 
        sx={{ 
          height: 4, 
          width: '100%', 
          bgcolor: theme.palette.primary.main,
          opacity: 0.8
        }} 
      />

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Title Section */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            <AssignmentIndIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Fulfillment Details
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Staff assignment and packing information
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={3}>
          {/* Assigned Staff Section */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <PersonIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Staff Assignment
              </Typography>
            </Stack>
            
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderColor: theme.palette.divider,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: fulfillment.assigned_to 
                      ? alpha(theme.palette.success.main, 0.1)
                      : alpha(theme.palette.grey[500], 0.1),
                    color: fulfillment.assigned_to 
                      ? theme.palette.success.main
                      : theme.palette.text.disabled,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Currently Assigned
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {fulfillment.assigned_to ? (
                      <>Staff #{fulfillment.assigned_to}</>
                    ) : (
                      <Box component="span" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                        Unassigned
                      </Box>
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>

          {/* Driver Assignment Section (only for local delivery) */}
          {isLocalDelivery && (
            <>
              <Divider sx={{ my: 1 }} />
              
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <DriveEtaIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Driver Assignment
                  </Typography>
                </Stack>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.02),
                    borderColor: alpha(theme.palette.warning.main, 0.2),
                  }}
                >
                  {fulfillment.delivery_driver_name ? (
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            color: theme.palette.warning.main,
                          }}
                        >
                          <DriveEtaIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                            Assigned Driver
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {fulfillment.delivery_driver_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {formatPhone(fulfillment.delivery_driver_phone)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ) : (
                    <Typography sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                      No driver assigned yet
                    </Typography>
                  )}

                  {fulfillment.status === "READY" && (
                    <>
                      <FormControl fullWidth size="small" disabled={driverLoading || actionLoading}>
                        <InputLabel id="driver-select-label">Select Driver</InputLabel>
                        <Select
                          labelId="driver-select-label"
                          label="Select Driver"
                          value={selectedDriverId}
                          onChange={(e) => onDriverChange(Number(e.target.value))}
                          sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.warning.main, 0.3),
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.warning.main,
                            },
                          }}
                        >
                          {drivers.map((d) => (
                            <MenuItem key={d.id} value={d.id}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <DriveEtaIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                                <span>{d.name}</span>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                  — {d.phone}
                                </Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        sx={{ 
                          mt: 2, 
                          borderRadius: 2, 
                          textTransform: "none",
                          fontWeight: 600,
                          background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                          color: theme.palette.warning.contrastText,
                          '&:hover': {
                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`,
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.2s ease',
                          '&:disabled': {
                            background: theme.palette.action.disabledBackground
                          }
                        }}
                        variant="contained"
                        fullWidth
                        disabled={!selectedDriverId || actionLoading || driverLoading}
                        onClick={onAssignDriver}
                        startIcon={<LocalShippingIcon />}
                      >
                        {driverLoading ? 'Loading drivers...' : 'Assign Driver'}
                      </Button>
                    </>
                  )}
                </Paper>
              </Box>
            </>
          )}

          {/* Packing Notes Section */}
          <Divider sx={{ my: 1 }} />
          
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <NotesIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Packing Notes
                </Typography>
              </Stack>
              {onUpdateNotes && !isEditingNotes && (
                <Tooltip title="Edit notes">
                  <IconButton 
                    size="small" 
                    onClick={() => setIsEditingNotes(true)}
                    sx={{
                      color: theme.palette.info.main,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.info.main, 0.1)
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            
            {isEditingNotes ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderColor: theme.palette.info.main,
                  bgcolor: alpha(theme.palette.info.main, 0.02)
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add packing notes..."
                  disabled={savingNotes}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
                <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancelNotes}
                    disabled={savingNotes}
                    startIcon={<CloseIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    startIcon={<SaveIcon />}
                    sx={{ 
                      borderRadius: 2, 
                      textTransform: 'none',
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                    }}
                  >
                    {savingNotes ? "Saving..." : "Save Notes"}
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  minHeight: "80px",
                  borderColor: fulfillment.packing_notes ? theme.palette.divider : alpha(theme.palette.info.main, 0.3),
                  borderStyle: fulfillment.packing_notes ? "solid" : "dashed",
                  bgcolor: fulfillment.packing_notes 
                    ? alpha(theme.palette.background.default, 0.5)
                    : alpha(theme.palette.info.main, 0.02),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.info.main,
                    bgcolor: alpha(theme.palette.info.main, 0.02)
                  }
                }}
              >
                <Typography 
                  sx={{ 
                    fontWeight: fulfillment.packing_notes ? 500 : 400,
                    color: fulfillment.packing_notes ? "text.primary" : "text.secondary",
                    fontStyle: fulfillment.packing_notes ? "normal" : "italic",
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {fulfillment.packing_notes || "No packing notes added yet. Click the edit icon to add notes."}
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Status Chip (for quick reference) */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Chip
              label={`Status: ${fulfillment.status}`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                color: theme.palette.text.secondary,
                borderRadius: 1.5,
                height: 24,
                '& .MuiChip-label': {
                  fontSize: '0.7rem',
                  fontWeight: 500
                }
              }}
            />
          </Box>
        </Stack>
      </CardContent>
    </Paper>
  );
};